import { Component, EventEmitter, OnInit, Output, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SaleFormService } from '@app/features/sales/add-sale/steps/sale-form.service';
import { CountryService } from '@src/app/shared/services/countries-service';
import { CustomersService, CustomerRequest, SalesService, RetailersService } from '@src/app/shared/backend/clients';
import { firstValueFrom, Subscription } from 'rxjs';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { GenderService } from '@src/app/shared/services/customerGender.service';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { SessionService } from '@src/app/shared/services/session.service';

@Component({
    selector: 'app-customer-details',
    templateUrl: './customer-details.component.html',
    styleUrl: './customer-details.component.scss',
    host: {
        class: 'app-customer-details'
    },
    standalone: false
})
export class StepCustomerDetailsComponent implements OnInit, OnDestroy {

  @Output() stepSaved = new EventEmitter<void>();

  customerTypeSubscription: Subscription | null = null;
  isSubmitDisabled: boolean = true;

  canAssignCustomer: boolean = false;
  assignSaleToCustomer: boolean = false;

  currentRetailerId: any;
  currentDate: Date = new Date();
  countries: any[] = [];

  companyLinks = {
    brandPrivacy: '',
    brandTerms: ''
  };

  comboData: ComboData = new ComboData(async (filter) => await firstValueFrom(this.customersService.apiCustomersFilterComboPost(filter)));

  customerDetailsFormGroup: FormGroup = this.fb.group({
    assignSaleToCustomer: [false],
    customerType: ['old', [Validators.required]],
    customer: [null],
    customerId: [[]],
    firstname: ['', [Validators.maxLength(50)]],
    lastname: ['', [Validators.maxLength(50)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    romanisedLastName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    email: [[], [emailRegexValidator(), Validators.maxLength(50)]],
    country: [null, [Validators.maxLength(50)]],
    address: ['', [Validators.maxLength(50)]],
    postalCode: ['', [Validators.maxLength(50)]],
    birthDate: [null, [Validators.maxLength(10)]],
    gender: [[], [Validators.maxLength(50)]],
    foreign: [false],
    wantEmails: [false],
    acceptedTerms: [false, [Validators.required]],
    acceptedPrivacyPolicy: [false, [Validators.required]],
    retailerId: [null]
  });

  genders: any = [];

  constructor(
    private fb: FormBuilder,
    private sessionService: SessionService,
    private countryService: CountryService,
    private genderService: GenderService,
    private saleFormService: SaleFormService,
    private customersService: CustomersService,
    private retailersService: RetailersService,
    public salesService: SalesService) { }

  async ngOnInit() {
    const company = await this.sessionService.getCompanyData();

    this.companyLinks = {
      brandPrivacy: company.privacyPolicyLink ?? '',
      brandTerms: company.termsOfUseLink ?? ''
    };

    const stepData = this.saleFormService.getStepData('sellerData');
    this.currentRetailerId = stepData.retailerId;
    this.canAssignCustomer = (await firstValueFrom(this.salesService.apiSalesCanAssignCustomerRetailerIdGet(parseInt(stepData.retailerId, 10)))).response ?? false;

    if (this.canAssignCustomer) {
      await this.initComboData();

      this.countries = await this.countryService.getCountries();
      this.genders = this.genderService.getGenderOptions();
      this.initFormStep();

      this.customerDetailsFormGroup.get('assignSaleToCustomer')?.valueChanges.subscribe(value => {
        this.assignSaleToCustomer = value;
      });

      const customerTypeControl = this.customerDetailsFormGroup.get('customerType');
      if (customerTypeControl) {
        this.customerTypeSubscription = customerTypeControl.valueChanges.subscribe(value => {
          this.toggleMandatoryFieldsValidation(value);
          this.customerDetailsFormGroup.get('customerId')?.reset([]);
        });
      } else {
        this.customerTypeSubscription = null;
      }

      this.toggleMandatoryFieldsValidation(this.customerDetailsFormGroup.get('customerType')?.value);
    }
  }

  private async initComboData() {

    const response = await firstValueFrom(this.retailersService.apiRetailersIdCustomerIdsGet(this.currentRetailerId));
    const customerIds = response.customerIds ?? [];
    this.comboData.additionalFilters = [
      { propertyName: 'Id', operator: FilterOperator.IN, values: customerIds.map((id: number)=> id.toString()) }
    ];
    await this.comboData.getData(null, 0);
  }

  async onLazyLoadComboData(event: any) {

    const response = await firstValueFrom(this.retailersService.apiRetailersIdCustomerIdsGet(this.currentRetailerId));
    const customerIds = response.customerIds ?? [];
    this.comboData.additionalFilters = [
      { propertyName: 'Id', operator: FilterOperator.IN, values: customerIds.map((id: number)=> id.toString()) }
    ];

    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.comboData.getData(search, activePage);
  }

  getComboTotalPages(): number {
    const totalRecords = this.comboData?.totalRecords ?? 0;
    const pageSize = Number(this.comboData?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  private ensureComboOption(option: any | null): void {
    if (!option?.id) {
      return;
    }

    const list = this.comboData.list ?? [];
    const exists = list.some((x: any) => String(x?.id) === String(option.id));

    if (!exists) {
      this.comboData.list = [option, ...list];
    }
  }

  private getSelectedCustomerOption(): any | null {
    const selectedId = this.customerDetailsFormGroup.get('customerId')?.value?.[0];

    if (!selectedId) {
      return null;
    }

    return (this.comboData.list ?? []).find((x: any) => String(x?.id) === String(selectedId)) ?? null;
  }

  toggleMandatoryFieldsValidation(customerType: string) {
    const customerControl = this.customerDetailsFormGroup.get('customerId');
    const emailControl = this.customerDetailsFormGroup.get('email');
    const firstnameControl = this.customerDetailsFormGroup.get('firstname');
    const lastnameControl = this.customerDetailsFormGroup.get('lastname');

    if (customerType === 'new') {
      customerControl?.clearValidators();
      customerControl?.setValue([], { emitEvent: false });
      customerControl?.disable({ emitEvent: false });

      emailControl?.setValidators([Validators.required, Validators.maxLength(50)]);
      firstnameControl?.setValidators([Validators.required, Validators.maxLength(50)]);
      lastnameControl?.setValidators([Validators.required, Validators.maxLength(50)]);
    } else {
      customerControl?.enable({ emitEvent: false });
      customerControl?.setValidators([Validators.required]);

      emailControl?.setValidators([Validators.maxLength(50)]);
      firstnameControl?.setValidators([Validators.maxLength(50)]);
      lastnameControl?.setValidators([Validators.maxLength(50)]);
    }

    customerControl?.updateValueAndValidity();
    emailControl?.updateValueAndValidity();
    firstnameControl?.updateValueAndValidity();
    lastnameControl?.updateValueAndValidity();
  }

  async initFormStep() {
    const stepData = this.saleFormService.getStepData('customerData');

    if (!stepData) {
      return;
    }

    this.customerDetailsFormGroup.controls['assignSaleToCustomer'].setValue(true);
    this.assignSaleToCustomer = true;

    const customerType = stepData.customerType ?? (stepData.customerId ? 'old' : 'new');
    this.customerDetailsFormGroup.controls['customerType'].setValue(customerType);

    if (customerType === 'new') {
      this.customerDetailsFormGroup.patchValue({
        firstname: stepData.customer?.firstName ?? '',
        lastname: stepData.customer?.lastName ?? '',
        romanisedFirstName: stepData.customer?.romanisedFirstName ?? '',
        romanisedLastName: stepData.customer?.romanisedLastName ?? '',
        email: stepData.customer?.email ?? '',
        country: stepData.customer?.isoCountry ? [stepData.customer.isoCountry] : null,
        address: stepData.customer?.address ?? '',
        postalCode: stepData.customer?.postalCode ?? '',
        birthDate: stepData.customer?.birthDate ? new Date(stepData.customer.birthDate) : null,
        gender: stepData.customer?.gender ? [stepData.customer.gender] : [],
        foreign: stepData.customer?.foreign ?? false,
        wantEmails: stepData.customer?.wantEmails ?? false,
        acceptedTerms: stepData.customer?.acceptedTerms ?? false,
        acceptedPrivacyPolicy: stepData.customer?.acceptedPrivacyPolicy ?? false
      });

      return;
    }

    this.ensureComboOption(stepData.customer ?? null);

    const selectedCustomerId = stepData.customerId ?? stepData.customer?.id;
    this.customerDetailsFormGroup.controls['customerId'].setValue(
      selectedCustomerId != null ? [selectedCustomerId] : []
    );
  }

  async onSaveStepData() {
    let customerData;

    if (this.assignSaleToCustomer) {
      if (this.customerDetailsFormGroup.controls['customerType'].value === 'new') {

        const birthDate = this.customerDetailsFormGroup.controls['birthDate'].value;
        const birthDateUtc = birthDate?.toISOString() || null;

        const payload: CustomerRequest = {
            firstName: this.customerDetailsFormGroup.controls['firstname'].value,
            lastName: this.customerDetailsFormGroup.controls['lastname'].value,
            romanisedFirstName: this.customerDetailsFormGroup.controls['romanisedFirstName'].value,
            romanisedLastName: this.customerDetailsFormGroup.controls['romanisedLastName'].value,
            email: this.customerDetailsFormGroup.controls['email'].value,
            isoCountry: this.customerDetailsFormGroup.controls['country'].value?.[0],
            address: this.customerDetailsFormGroup.controls['address'].value,
            postalCode: this.customerDetailsFormGroup.controls['postalCode'].value,
            birthDate: birthDateUtc,
            gender: this.customerDetailsFormGroup.controls['gender'].value?.[0],
            foreign: this.customerDetailsFormGroup.controls['foreign'].value,
            wantEmails: this.customerDetailsFormGroup.controls['wantEmails'].value,
            acceptedTerms: this.customerDetailsFormGroup.controls['acceptedTerms'].value,
            acceptedPrivacyPolicy: this.customerDetailsFormGroup.controls['acceptedPrivacyPolicy'].value,
            retailerId: this.currentRetailerId
          };

          // Create customer on the go and get customerId
        customerData = {
          customerType: 'new',
          customer: payload,
          customerName: `${this.customerDetailsFormGroup.controls['firstname'].value} ${this.customerDetailsFormGroup.controls['lastname'].value} (${this.customerDetailsFormGroup.controls['email'].value})`
        };

      }
      else {
        const selectedCustomerId = this.customerDetailsFormGroup.controls['customerId'].value?.[0];
        const selectedCustomer = this.getSelectedCustomerOption();
        customerData = {
          customerId: selectedCustomerId,
          customerName: selectedCustomer?.text
        };
      }
    }
    else {
      customerData = null;
    }
    this.saleFormService.setStepData('customerData', customerData);
    this.stepSaved.emit();
  }

  async getCanAssignCustomer(){
  }

  ngOnDestroy() {
    if (this.customerTypeSubscription) {
      this.customerTypeSubscription.unsubscribe();
    }
  }
}
